import { isInitParentMessage, isUpdateSrcMessage } from './messages.js';
const windowEventTypes = [
    'animationstart',
    'webkitAnimationStart',
    'animationiteration',
    'webkitAnimationIteration',
    'animationend',
    'webkitAnimationEnd',
    'input',
    'mouseup',
    'mousedown',
    'orientationchange',
    'afterprint',
    'beforeprint',
    'readystatechange',
    'touchstart',
    'touchend',
    'touchcancel',
    'transitionstart',
    'webkitTransitionStart',
    'MSTransitionStart',
    'oTransitionStart',
    'otransitionstart', 'transitioniteration',
    'webkitTransitionIteration',
    'MSTransitionIteration',
    'oTransitionIteration',
    'otransitioniteration', 'transitionend',
    'webkitTransitionEnd',
    'MSTransitionEnd',
    'oTransitionEnd',
    'otransitionend', 'resize'
];
export default class DFrameContent {
    initialized = false;
    options;
    pendingCheckHeight = false;
    debug = false;
    id;
    throttledCheckHeight;
    lastHeight = 0;
    constructor(options) {
        this.options = options ?? {};
        const afCallback = () => {
            this.checkHeight();
            this.pendingCheckHeight = false;
        };
        this.throttledCheckHeight = () => {
            if (this.pendingCheckHeight)
                return;
            this.pendingCheckHeight = true;
            requestAnimationFrame(afCallback);
        };
        if (typeof window === 'undefined')
            return;
        window.addEventListener('message', (e) => this.onMessage(e));
        // simple handshake for initialization
        this.postMessageToParent(['df-child', 'init']);
    }
    log(level, ...args) {
        if (level === 'debug' && !this.debug)
            return;
        if (level === 'debug')
            console.timeLog(`d-frame:${this.id}:content`, ...args);
        if (level === 'info')
            console.info(`d-frame:${this.id}:content`, ...args);
        if (level === 'error')
            console.error(`d-frame:${this.id}:content`, ...args);
    }
    onMessage(e) {
        if (e.source === window.parent && Array.isArray(e.data)) {
            const message = e.data;
            if (isInitParentMessage(message)) {
                this.id = message[2].id;
                this.debug = !!message[2].debug;
                if (this.debug)
                    console.time(`d-frame:${this.id}:content`);
                if (message[2].resize !== 'no')
                    this.initResize();
                if (message[2].syncParams || message[2].syncPath || message[2].stateChangeEvents)
                    this.initStateChangeWatcher();
                this.initialized = true;
            }
            if (isUpdateSrcMessage(message)) {
                if (this.options.updateSrc) {
                    this.options.updateSrc(message[2].startsWith('/') ? window.location.origin + message[2] : message[2], this);
                }
                else {
                    window.location.href = message[2];
                }
            }
        }
    }
    postMessageToParent(message) {
        window.parent.postMessage(message);
    }
    sendNotif(notif) {
        this.postMessageToParent(['df-child', 'notif', notif]);
    }
    sendMessage(msg) {
        this.postMessageToParent(['df-child', 'custom', msg]);
    }
    ready() {
        this.postMessageToParent(['df-child', 'ready']);
    }
    initResize() {
        this.log('debug', 'initResize');
        this.checkHeight();
        this.createMutationObserver();
        this.createWindowEventListeners();
    }
    initStateChangeWatcher() {
        this.log('debug', 'initStateChangeWatcher');
        // monkey patch pushState and replaceState to send all state change info to the parent window
        // this is to compensate the lack of event emission by window.history
        const oldReplaceState = window.history.replaceState;
        const newPushState = (...args) => {
            // do a replace instead of a push, the push will be done in the parent window if sync-state is activated
            const ret = oldReplaceState.apply(window.history, args);
            this.postMessageToParent(['df-child', 'stateChange', 'push', window.location.href]);
            return ret;
        };
        window.history.pushState = newPushState;
        const newReplaceState = (...args) => {
            const ret = oldReplaceState.apply(window.history, args);
            this.postMessageToParent(['df-child', 'stateChange', 'replace', window.location.href]);
            return ret;
        };
        window.history.replaceState = newReplaceState;
    }
    createMutationObserver() {
        const body = document.querySelector('body');
        if (!body)
            throw new Error('DFrameContentManager was initialized before the HTML body');
        const observer = new window.MutationObserver(this.throttledCheckHeight);
        observer.observe(body, {
            attributes: false,
            attributeOldValue: false,
            characterData: false,
            characterDataOldValue: false,
            childList: true,
            subtree: true,
        });
    }
    createWindowEventListeners() {
        for (const eventType of windowEventTypes) {
            window.addEventListener(eventType, this.throttledCheckHeight, { passive: true });
        }
    }
    checkHeight() {
        const elements = document.querySelectorAll('[data-iframe-height]');
        let maxWithoutOffset = 0;
        let max = 0;
        for (const element of elements) {
            const dataAttribute = element.getAttribute('data-iframe-height');
            const bottomWithoutOffset = element.getBoundingClientRect().bottom +
                parseFloat(getComputedStyle(element).getPropertyValue('margin-bottom'));
            const bottom = dataAttribute ? (bottomWithoutOffset + parseFloat(dataAttribute)) : bottomWithoutOffset;
            if (bottomWithoutOffset > maxWithoutOffset)
                maxWithoutOffset = bottomWithoutOffset;
            if (bottom > max)
                max = bottom;
        }
        max = Math.ceil(max);
        if (maxWithoutOffset > 0 && max !== this.lastHeight) {
            this.postMessageToParent(['df-child', 'height', max]);
            this.lastHeight = max;
        }
    }
}
